import { cn } from "@/lib/utils";
import { FREIGHT_STATES, type FreightState } from "@/lib/flowUi";

export interface FreightStateRailProps {
  current: FreightState;
  /** States already proven, e.g. ["planned"]. */
  completed?: FreightState[];
  className?: string;
}

const LABELS: Record<FreightState, string> = {
  planned: "Planned",
  moving: "Moving",
  proven: "Proven",
  attention: "Attention",
  blocked: "Blocked",
};

const DOT: Record<FreightState, string> = {
  planned: "bg-flow-indigo",
  moving: "bg-flow-cyan",
  proven: "bg-flow-proven",
  attention: "bg-flow-attention",
  blocked: "bg-flow-blocked",
};

/** Freight State Rail — one horizontal read of where a load actually stands. */
export function FreightStateRail({ current, completed = [], className }: FreightStateRailProps) {
  // Exception states are shown on their own; they are not lifecycle steps.
  const isException = current === "attention" || current === "blocked";
  const steps = isException ? ([...FREIGHT_STATES.slice(0, 3), current] as FreightState[]) : FREIGHT_STATES.slice(0, 3);

  return (
    <ol className={cn("flex items-center gap-2", className)} aria-label="Freight state">
      {steps.map((state) => {
        const active = state === current;
        const done = completed.includes(state);
        return (
          <li key={state} className="flex items-center gap-2">
            <span
              aria-hidden
              className={cn(
                "h-2 w-2 rounded-full transition-colors duration-quick ease-flow",
                active || done ? DOT[state] : "bg-muted",
                active && state === "moving" && "flow-pulse",
              )}
            />
            <span
              className={cn(
                "text-xs",
                active ? "font-semibold text-foreground" : done ? "text-foreground/70" : "text-muted-foreground",
              )}
              aria-current={active ? "step" : undefined}
            >
              {LABELS[state]}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export default FreightStateRail;
